import React, { useEffect, useState } from "react";
import { Table, Typography } from "antd";
import dayjs from "dayjs";
import api from "../../../services/axiosInstance";
import GenericReportPage from "../../../components/common/GenericReportPage";
import { fmtMoney } from "../../../utils/reportExport";

const STATUS_OPTIONS = [
    { value: "", label: "All Payment Status" },
    { value: "Pending", label: "Pending" },
    { value: "Approved", label: "Approved" },
    { value: "Partially Paid", label: "Partially Paid" },
    { value: "Paid", label: "Paid" },
    { value: "Rejected", label: "Rejected" },
    { value: "Cancel", label: "Cancel" },
];

const APPROVAL_OPTIONS = [
    { value: "", label: "All Approval Status" },
    { value: "Pending", label: "Pending" },
    { value: "Approved", label: "Approved" },
    { value: "Rejected", label: "Rejected" },
    { value: "Cancel", label: "Cancel" },
];

const ExpensesReport = () => {
    const [categories, setCategories] = useState([]);
    const [suppliers, setSuppliers] = useState([]);

    useEffect(() => {
        let mounted = true;
        api.get('/masterdropdown/listof_activeexpensecategories.php')
            .then((res) => { if (mounted) setCategories(res.data.dataset || []); })
            .catch(() => { /* Non-critical */ });
        api.get('/masterdropdown/listof_activesuppliers.php')
            .then((res) => { if (mounted) setSuppliers(res.data.dataset || []); })
            .catch(() => { /* Non-critical */ });
        return () => { mounted = false; };
    }, []);

    return (
        <GenericReportPage
            title="Expenses Report"
            subtitle="Expense register with category, supplier, approval and payment status"
            breadcrumb={["Reports", "Expenses"]}
            fetchUrl="/reports/expenses/expenses.php"
            rowKey="exp_id"
            filterDefs={[
                { type: "search", key: "search", label: "Search", col: 3, placeholder: "Ref, title, ref" },
                { type: "select", key: "expcat_id", label: "Category", col: 3, options: categories, showSearch: true },
                { type: "select", key: "sup_id", label: "Supplier", col: 3, options: suppliers, showSearch: true },
                { type: "select", key: "status", label: "Status", col: 2, options: STATUS_OPTIONS },
                { type: "select", key: "approval", label: "Approval", col: 2, options: APPROVAL_OPTIONS },
                { type: "date", key: "dateRange", label: "Request Date", col: 5 },
            ]}
            columns={[
                { title: 'Exp Ref', dataIndex: 'exp_ref', key: 'exp_ref', width: 125, sorter: (a, b) => (a.exp_ref || '').localeCompare(b.exp_ref || '') },
                { title: 'Title', dataIndex: 'exp_title', key: 'exp_title', sorter: (a, b) => (a.exp_title || '').localeCompare(b.exp_title || '') },
                { title: 'Category', dataIndex: 'expcat_name', key: 'expcat_name', width: 150, render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
                { title: 'Supplier', dataIndex: 'supplier_name', key: 'supplier_name', width: 160, render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
                { title: 'Amount', dataIndex: 'exp_amount', key: 'exp_amount', align: 'right', width: 120, sorter: (a, b) => (a.exp_amount || 0) - (b.exp_amount || 0), render: (v) => fmtMoney(v) },
                { title: 'Request Date', dataIndex: 'payment_req_date', key: 'payment_req_date', width: 115, render: (d) => dayjs(d).format("YYYY-MM-DD"), sorter: (a, b) => dayjs(a.payment_req_date).valueOf() - dayjs(b.payment_req_date).valueOf() },
                { title: 'Approval', dataIndex: 'approved_status', key: 'approved_status', width: 110 },
                { title: 'Payment Status', dataIndex: 'exp_status', key: 'exp_status', width: 120 },
                { title: 'Created By', dataIndex: 'creator_name', key: 'creator_name', width: 130 },
            ]}
            tableSummary={(pageData) => (
                <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={4}><b>Total ({pageData.length} expenses)</b></Table.Summary.Cell>
                    <Table.Summary.Cell index={4} align="right"><b>{fmtMoney(pageData.reduce((s, r) => s + Number(r.exp_amount || 0), 0))}</b></Table.Summary.Cell>
                    <Table.Summary.Cell index={5} colSpan={4} />
                </Table.Summary.Row>
            )}
            pdf={{
                title: "EXPENSES REPORT",
                subtitle: "Expense register with category, supplier, approval and payment status",
                columns: [
                    { header: "Exp Ref" }, { header: "Title" }, { header: "Category" }, { header: "Supplier" },
                    { header: "Amount", align: "right" }, { header: "Request Date" },
                    { header: "Approval" }, { header: "Payment Status" }, { header: "Created By" },
                ],
                rows: (rows) =>
                    rows.map((r) => [
                        r.exp_ref || "-",
                        r.exp_title || "-",
                        r.expcat_name || "-",
                        r.supplier_name || "-",
                        fmtMoney(r.exp_amount),
                        dayjs(r.payment_req_date).format("YYYY-MM-DD"),
                        r.approved_status || "-",
                        r.exp_status || "-",
                        r.creator_name || "-",
                    ]),
                summary: (rows) => [
                    { label: "Total Expense Amount:", value: fmtMoney(rows.reduce((s, r) => s + Number(r.exp_amount || 0), 0)) },
                ],
                totalRow: (rows) => [
                    "TOTAL", "", "", "", fmtMoney(rows.reduce((s, r) => s + Number(r.exp_amount || 0), 0)), "", "", "", "",
                ],
                columnStyles: { 4: { halign: "right" } },
            }}
            excel={{
                filename: "expenses-report",
                sheetName: "Expenses",
                columns: [
                    { title: "Exp Ref", dataIndex: "exp_ref", width: 14 },
                    { title: "Title", dataIndex: "exp_title", width: 30 },
                    { title: "Category", dataIndex: "expcat_name", width: 18 },
                    { title: "Supplier", dataIndex: "supplier_name", width: 20 },
                    { title: "Amount", dataIndex: "exp_amount", width: 14 },
                    { title: "Request Date", dataIndex: "payment_req_date", width: 13 },
                    { title: "Approval", dataIndex: "approved_status", width: 12 },
                    { title: "Payment Status", dataIndex: "exp_status", width: 14 },
                    { title: "Created By", dataIndex: "creator_name", width: 18 },
                ],
                rows: (rows) =>
                    rows.map((r) => ({
                        ...r,
                        exp_amount: fmtMoney(r.exp_amount),
                        payment_req_date: dayjs(r.payment_req_date).format("YYYY-MM-DD"),
                    })),
                extraRows: (rows) => [["TOTAL", "", "", "", fmtMoney(rows.reduce((s, r) => s + Number(r.exp_amount || 0), 0)), "", "", "", ""]],
            }}
        />
    );
};

export default ExpensesReport;
